import { useSetToast } from "@app/components/Toast";
import { useS3FilesManagement } from "@app/hooks/createFileHook";
import LocaleChipSelect from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/components/QuestionModal/LocaleChipSelect";
import { SupportedLocales } from "@app/shared/utils/locales";
import {
    newS3FileToCreateFileInput,
    type NewS3File,
} from "@app/usecases/FileUseCases";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import DropZone from "@design-system/Inputs/DropZone";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { useMemo, useState, type FC } from "react";
import { useGenerateEvsWorkplan } from "../../../data";
import { useGenerateEvsWorkplanModalTranslations } from "./i18n";

export const GenerateEvsWorkplanModal: FC = () => {
    const { t, keys } = useGenerateEvsWorkplanModalTranslations();
    const [open, setOpen] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<NewS3File[]>([]);

    const [selectedThemes, setSelectedThemes] = useState<
        (string | undefined)[]
    >([undefined, undefined, undefined, undefined]);

    const [locale, setLocale] = useState<SupportedLocales>(SupportedLocales.EN);

    const THEMES = [
        {
            id: "environment",
            name: t(keys.theme_environment),
        },
        {
            id: "ethics",
            name: t(keys.theme_ethics),
        },
        {
            id: "procurement",
            name: t(keys.theme_procurement),
        },
        {
            id: "social",
            name: t(keys.theme_social),
        },
        {
            id: "ecovadis",
            name: t(keys.theme_ecovadis_questionnaire),
        },
    ];

    const { generateEvsWorkplan, isPending: isEvsPending } =
        useGenerateEvsWorkplan();
    const { setToastSuccess, setToastError } = useSetToast();

    const { uploadFiletoS3 } = useS3FilesManagement();

    const handleFileDrop = async (files: File[]) => {
        for (const file of files) {
            const newS3File = await uploadFiletoS3(file);
            setUploadedFiles((prev) => [...prev, newS3File]);
        }
    };

    const handleDeleteFile = (file: NewS3File) => () => {
        setUploadedFiles((prev) => prev.filter((f) => f.key !== file.key));
    };

    const handleEvsWorkplan = async () => {
        if (uploadedFiles.length === 5) {
            generateEvsWorkplan(
                {
                    files: uploadedFiles.map((file, index) => {
                        const theme = THEMES.find(
                            (theme) => theme.id === selectedThemes[index],
                        );
                        return {
                            file: newS3FileToCreateFileInput(file),
                            theme: theme?.id ?? "",
                        };
                    }),
                    locale: locale,
                },
                {
                    onSuccess: () => {
                        setToastSuccess(t(keys.generate_workplan_success));
                        setOpen(false);
                        setUploadedFiles([]);
                    },
                    onError: () => {
                        setToastError(t(keys.generate_workplan_error));
                    },
                },
            );
        }
    };
    const handleCancel = (open: boolean) => () => {
        setOpen(open);
        setUploadedFiles([]);
        setSelectedThemes([undefined, undefined, undefined, undefined]);
    };

    const isAllThemesUsed = useMemo(() => {
        const validSelectedThemes = selectedThemes.filter(Boolean);
        return (
            validSelectedThemes.length === THEMES.length &&
            THEMES.every((theme) => validSelectedThemes.includes(theme.id))
        );
    }, [selectedThemes]);

    const acceptedFileTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/pdf",
    ];

    return (
        <Modal open={open} onOpenChange={handleCancel(!open)}>
            <Modal.Trigger asChild>
                <FlexRow alignItems="center" gap="2" className="cursor-pointer">
                    <Icon name="stars" color="#BE2FBE" size="sm" />
                    {t(keys.open_button)}
                </FlexRow>
            </Modal.Trigger>
            <Modal.Overlay centerContent className="py-5" scrollable>
                <Modal.Content className="w-fit max-w-[880px]">
                    <Modal.Header>
                        <Modal.Title>{t(keys.generate_workplan)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="4">
                            <Text variant="body" color="secondary">
                                {t(keys.generate_workplan_description)}
                            </Text>
                            <FlexRow alignItems="center" gap="2">
                                <Text variant="body-bold">
                                    {t(keys.select_language)}
                                </Text>
                                <LocaleChipSelect
                                    locale={locale}
                                    setLocale={setLocale}
                                    availableLocales={[
                                        SupportedLocales.EN,
                                        SupportedLocales.FR,
                                    ]}
                                />
                            </FlexRow>
                            <FlexCol gap="2">
                                <Text variant="body-bold">
                                    {t(keys.upload_evs_scorecards)}
                                </Text>
                                {uploadedFiles.length > 0 && (
                                    <FlexCol gap="2" className="w-full">
                                        {uploadedFiles.map((file, index) => (
                                            <FlexRow
                                                justifyContent="between"
                                                alignItems="center"
                                                gap="2"
                                                key={`${file.key}-${index}`}
                                            >
                                                <FlexRow
                                                    justifyContent="start"
                                                    alignItems="center"
                                                >
                                                    <Text variant="body">
                                                        {file.name}
                                                    </Text>
                                                    <Button
                                                        variant="plain"
                                                        size="sm"
                                                        onClick={handleDeleteFile(
                                                            file,
                                                        )}
                                                    >
                                                        <Button.Icon name="trash" />
                                                    </Button>
                                                </FlexRow>
                                                <ThemeDropdown
                                                    index={index}
                                                    selectedThemes={
                                                        selectedThemes
                                                    }
                                                    setSelectedThemes={
                                                        setSelectedThemes
                                                    }
                                                    themes={THEMES}
                                                />
                                            </FlexRow>
                                        ))}
                                    </FlexCol>
                                )}
                                <DropZone
                                    accept={acceptedFileTypes}
                                    buttonText="Upload"
                                    multiple={true}
                                    size="lg"
                                    className="w-full h-full"
                                    onChange={handleFileDrop}
                                    loading={false}
                                >
                                    <Text variant="body-bold">
                                        {t(keys.upload_evs_scorecards)}
                                    </Text>
                                </DropZone>
                            </FlexCol>
                        </FlexCol>
                        <FlexRow justifyContent="end" gap="2" w="full" mt="4">
                            <Button
                                variant="outlined"
                                size="sm"
                                onClick={handleCancel(false)}
                            >
                                {t(keys.cancel)}
                            </Button>
                            <Tooltip>
                                <Tooltip.Trigger>
                                    <Button
                                        variant="flatPrimary"
                                        size="sm"
                                        onClick={handleEvsWorkplan}
                                        disabled={
                                            uploadedFiles.length !== 5 ||
                                            !isAllThemesUsed
                                        }
                                        loading={isEvsPending}
                                    >
                                        {t(keys.generate_workplan)}
                                    </Button>
                                </Tooltip.Trigger>
                                {(uploadedFiles.length !== 5 ||
                                    !isAllThemesUsed) && (
                                    <Tooltip.Content>
                                        {t(keys.generate_workplan_tooltip)}
                                    </Tooltip.Content>
                                )}
                            </Tooltip>
                        </FlexRow>
                    </Modal.Body>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

const ThemeDropdown: FC<{
    index: number;
    selectedThemes: (string | undefined)[];
    setSelectedThemes: (value: (string | undefined)[]) => void;
    themes: {
        id: string;
        name: string;
    }[];
}> = ({ index, selectedThemes, setSelectedThemes, themes }) => {
    const { t, keys } = useGenerateEvsWorkplanModalTranslations();

    const handleThemeChange = (value: string[]) => {
        if (value.length > 0) {
            const newThemes = [...selectedThemes];
            newThemes[index] = value[0];
            setSelectedThemes(newThemes);
        }
        if (value.length === 0) {
            const newThemes = [...selectedThemes];
            newThemes[index] = undefined;
            setSelectedThemes(newThemes);
        }
    };

    const options = themes.map((theme) => ({
        value: theme.id,
        label: theme.name,
    }));

    return (
        <Dropdown
            onValueChange={handleThemeChange}
            selectType="single"
            value={selectedThemes[index] ? [selectedThemes[index]] : []}
        >
            <Dropdown.ChipFilterTrigger
                label={t(keys.select_theme)}
                options={options}
                valueIdentifierKey="value"
                valueDisplayKey="label"
            />
            <Dropdown.Content>
                {options.map((option) => (
                    <Dropdown.SelectableItem
                        value={option.value}
                        key={option.value}
                    >
                        <Dropdown.SelectedIndicator />
                        {option.label}
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};
